/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { ApplicationBranding, ApplicationBrandingLastUpdated } from '../service/types/ApplicationBranding';
import { LocalStorageHelper } from '../utils/LocalStorageHelper';
import { NOTIFICATION_TYPES, NotificationContext } from './NotificationContext';
import { AxiosResponse } from 'axios';
import publicServices from '../service/publicServices';

const LOCAL_STORAGE_APP_BRANDING = 'LOCAL_STORAGE_APP_BRANDING';

interface ApplicationBrandingContextType {
  isAppBrandingLoading: boolean;
  appBrand: ApplicationBranding | null;
}

export const ApplicationBrandingContext = React.createContext<ApplicationBrandingContextType>({
  isAppBrandingLoading: true,
  appBrand: null
});

export const ApplicationBrandingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isAppBrandingLoading, setIsAppBrandingLoading] = useState<boolean>(false);
  const [appBrand, setAppBrand] = useState<ApplicationBranding | null>(null);
  const { setType, setMessage } = useContext(NotificationContext);

  useEffect(() => {
    // Load branding data from public services - available before authentication
    // This allows the public homepage to display custom branding (banner, titles, etc.)
    setIsAppBrandingLoading(true);
    const cachedResponse = LocalStorageHelper.getLocalResponseData<ApplicationBranding[]>(LOCAL_STORAGE_APP_BRANDING);
    const hasBranding = cachedResponse !== null && cachedResponse.data.length > 0;

    const brandingUpdated = (
      cachedResponse: AxiosResponse<ApplicationBranding[]> | null,
      response: AxiosResponse<ApplicationBrandingLastUpdated>
    ): boolean => {
      if (cachedResponse === null || new Date(response.data.last_updated_timestamp).getTime() === 0) {
        return true;
      } else {
        return new Date(cachedResponse.data[0].last_updated_timestamp).getTime() < new Date(response.data.last_updated_timestamp).getTime();
      }
    };

    const getApplicationBranding = async (): Promise<void> => {
      try {
        const response = await publicServices.getApplicationBrandings(window.location.origin);
        if (response.data.length > 0) {
          setAppBrand(response.data[0]);
          LocalStorageHelper.setLocalResponseData(LOCAL_STORAGE_APP_BRANDING, response);
        } else {
          localStorage.removeItem(LOCAL_STORAGE_APP_BRANDING);
        }
      } catch (error: any) {
        setType(NOTIFICATION_TYPES.FAILED);
        setMessage(error.message);
      } finally {
        setIsAppBrandingLoading(false);
      }
    };

    const getApplicationBrandingTimestamp = async (): Promise<void> => {
      try {
        const response = await publicServices.getApplicationBrandingTimestamp(window.location.origin);
        if (brandingUpdated(cachedResponse, response)) {
          await getApplicationBranding();
        } else {
          setAppBrand(cachedResponse?.data[0] ?? null);
          setIsAppBrandingLoading(false);
        }
      } catch (error: any) {
        setType(NOTIFICATION_TYPES.FAILED);
        setMessage(error.message);
        setIsAppBrandingLoading(false);
      }
    };

    if (hasBranding) {
      getApplicationBrandingTimestamp();
    } else {
      getApplicationBranding();
    }
  }, []); // Run once on mount - no authentication dependency

  useEffect(() => {
    document.title = appBrand?.headers?.msc_title ?? '';
  }, [appBrand?.headers?.msc_title]);

  return <ApplicationBrandingContext.Provider value={{ isAppBrandingLoading, appBrand }}>{children}</ApplicationBrandingContext.Provider>;
};
